import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { layers } from 'app/spacing';
import { cloneElement, forwardRef } from 'react';

const fadeInCss = ({ fadeIn }) => css`
  transition: opacity 0.1s ease-in-out;
  opacity: ${fadeIn ? 1 : 0};
`;

const dimension = {
  top: 'height',
  right: 'width',
  bottom: 'height',
  left: 'width',
};

const GradientWrapper = styled.div`
  position: sticky;
  ${({ position }) => position}: 0;
  ${({ position }) => dimension[position]}: 0;
  ${({ position }) => {
    if (position === 'left' || position === 'right') {
      return css`
        top: 0;
      `;
    }
    return css`
      width: 100%;
    `;
  }}
  ${({ zIndex }) =>
    zIndex !== null
      ? css`
          z-index: ${zIndex};
        `
      : css`
          z-index: ${layers.content(0, 1)};
        `}

  overflow: visible;
  pointer-events: none;
  ${fadeInCss}
`;

const boundingPositions = {
  top: ['top', 'left', 'right'],
  right: ['right', 'top', 'bottom'],
  bottom: ['bottom', 'left', 'right'],
  left: ['left', 'top', 'bottom'],
};

const oppositePosition = {
  top: 'bottom',
  right: 'left',
  bottom: 'top',
  left: 'right',
};

export const GradientFade = styled.div`
  position: absolute;
  ${({ position }) => {
    const [b1, b2, b3] = boundingPositions[position];
    return css`
      ${b1}: 0;
      ${b2}: 0;
      ${b3}: 0;
    `;
  }}
  ${({ position, fadeStart, fadeLength }) => css`
    ${dimension[position]}: ${fadeStart + fadeLength}px
  `}
  ${({ borderRadius }) => css`
    border-radius: ${borderRadius};
  `}
/* Need to use rgba rather than "transparent" for safari's benefit */
background: linear-gradient(
  to ${({ position }) => oppositePosition[position]},
  ${({ color }) => color} ${({ fadeStart }) => fadeStart}px,
  ${({ color }) => `color-mix(in srgb, ${color} 0%, transparent)`}
);
`;

const positions = ['top', 'right', 'bottom', 'left'];

export function Gradient({
  color = 'var(--color-background-white)',
  position = 'bottom',
  fadeStart = 0,
  fadeLength = 50,
  borderRadius,
  ...others
}) {
  if (!positions.includes(position)) {
    return null;
  }
  return (
    <GradientWrapper position={position} {...others}>
      <GradientFade
        borderRadius={borderRadius}
        color={color}
        position={position}
        fadeStart={fadeStart}
        fadeLength={fadeLength}
      />
    </GradientWrapper>
  );
}

const GradientHorizontalContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const GradientVerticalContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const ScrollFadeContainer = forwardRef(function ScrollFadeContainer(
  {
    top,
    right,
    bottom,
    left,
    // Output from useEndsOfScroll()
    scrolled: [scrolledTop, scrolledRight, scrolledBottom, scrolledLeft],
    gradient,
    children,
    ...others
  },
  ref
) {
  return (
    <GradientHorizontalContainer {...others} ref={ref}>
      {!!left &&
        cloneElement(gradient, {
          position: 'left',
          fadeIn: !scrolledLeft,
          ...left,
        })}
      <GradientVerticalContainer className="gradient-vertical-container">
        {!!top &&
          cloneElement(gradient, {
            position: 'top',
            fadeIn: !scrolledTop,
            ...top,
          })}
        {children}
        {!!bottom &&
          cloneElement(gradient, {
            position: 'bottom',
            fadeIn: !scrolledBottom,
            ...bottom,
          })}
      </GradientVerticalContainer>
      {!!right &&
        cloneElement(gradient, {
          position: 'right',
          fadeIn: !scrolledRight,
          ...right,
        })}
    </GradientHorizontalContainer>
  );
});

ScrollFadeContainer.defaultProps = {
  top: false,
  right: false,
  bottom: false,
  left: false,
  scrolled: [false, false, false, false],
  gradient: <Gradient />,
};

export default ScrollFadeContainer;
